interface OriginLease {
  owner: string;
  expiresAt: number;
}

export interface OriginLockOptions {
  leaseMs?: number;
  retryMs?: number;
  maximumWaitMs?: number;
  futureSkewMs?: number;
}

const DEFAULT_LEASE_MS = 4_000;
const DEFAULT_RETRY_MS = 24;
const DEFAULT_FUTURE_SKEW_MS = 1_000;
const LOCK_DATABASE_NAME = 'nightshift-origin-locks';
const LOCK_DATABASE_VERSION = 1;
const LOCK_STORE_NAME = 'leases';

/**
 * Serialises a short origin-local mutation across tabs. Web Locks are used
 * where available; older/insecure Chromium builds use an atomic IndexedDB
 * lease. A final localStorage fallback keeps storage-restricted single-tab
 * sessions playable, but is never selected while IndexedDB is available.
 */
export function withOriginLock<T>(
  lockName: string,
  leaseKey: string,
  operation: () => T | Promise<T>,
  options: OriginLockOptions = {},
): Promise<T> {
  return defaultOriginLockCoordinator.run(lockName, leaseKey, operation, options);
}

/**
 * Separate instances model separate tabs in tests. The queue deliberately
 * remains instance-local; actual cross-context exclusion comes from Web Locks
 * or the IndexedDB read/write transaction below.
 */
export class OriginLockCoordinator {
  private readonly queues = new Map<string, Promise<void>>();

  run<T>(
    lockName: string,
    leaseKey: string,
    operation: () => T | Promise<T>,
    options: OriginLockOptions = {},
  ): Promise<T> {
    const previous = this.queues.get(lockName) ?? Promise.resolve();
    const result = previous.then(
      () => requestOriginLock(lockName, leaseKey, operation, options),
      () => requestOriginLock(lockName, leaseKey, operation, options),
    );
    const tail = result.then(() => undefined, () => undefined);
    this.queues.set(lockName, tail);
    void tail.finally(() => {
      if (this.queues.get(lockName) === tail) this.queues.delete(lockName);
    });
    return result;
  }
}

const defaultOriginLockCoordinator = new OriginLockCoordinator();

async function requestOriginLock<T>(
  lockName: string,
  leaseKey: string,
  operation: () => T | Promise<T>,
  options: OriginLockOptions,
): Promise<T> {
  const locks = typeof navigator === 'undefined'
    ? undefined
    : (navigator as Navigator & {
        locks?: {
          request<U>(
            name: string,
            options: { mode: 'exclusive'; signal?: AbortSignal },
            callback: () => U | Promise<U>,
          ): Promise<U>;
        };
      }).locks;
  if (locks && typeof locks.request === 'function') {
    const leaseMs = positive(options.leaseMs, DEFAULT_LEASE_MS);
    const maximumWaitMs = positive(options.maximumWaitMs, leaseMs + 1_000);
    const controller = new AbortController();
    let operationStarted = false;
    let timedOut = false;
    let timeout: ReturnType<typeof globalThis.setTimeout> | undefined;
    const acquisitionTimeout = new Promise<never>((_resolve, reject) => {
      timeout = globalThis.setTimeout(() => {
        timedOut = true;
        controller.abort();
        reject(new Error('Timed out waiting for local game-data lock'));
      }, maximumWaitMs);
    });
    try {
      const lockRequest = Promise.resolve().then(() => locks.request(
        lockName,
        { mode: 'exclusive', signal: controller.signal },
        () => {
          if (timedOut) throw new Error('Timed out waiting for local game-data lock');
          operationStarted = true;
          if (timeout !== undefined) globalThis.clearTimeout(timeout);
          timeout = undefined;
          return operation();
        },
      ));
      return await Promise.race([lockRequest, acquisitionTimeout]);
    } catch (error) {
      if (operationStarted) throw error;
      if (timedOut) throw new Error('Timed out waiting for local game-data lock');
    } finally {
      if (timeout !== undefined) globalThis.clearTimeout(timeout);
    }
  }
  if (typeof indexedDB !== 'undefined') {
    try {
      return await withIndexedDbLease(leaseKey, operation, options);
    } catch (error) {
      if (!(error instanceof OriginLockBackendUnavailableError)) throw error;
    }
  }
  return withStorageLease(leaseKey, operation, options);
}

async function withIndexedDbLease<T>(
  leaseKey: string,
  operation: () => T | Promise<T>,
  options: OriginLockOptions,
): Promise<T> {
  const leaseMs = positive(options.leaseMs, DEFAULT_LEASE_MS);
  const retryMs = positive(options.retryMs, DEFAULT_RETRY_MS);
  const maximumWaitMs = positive(options.maximumWaitMs, leaseMs + 1_000);
  const futureSkewMs = nonNegative(options.futureSkewMs, DEFAULT_FUTURE_SKEW_MS);
  const owner = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  const waitStartedAt = monotonicNow();

  while (monotonicNow() - waitStartedAt <= maximumWaitMs) {
    const now = Date.now();
    const acquired = await mutateIndexedDbLease(leaseKey, maximumWaitMs, (currentValue) => {
      const current = parseLeaseValue(currentValue, now, leaseMs, futureSkewMs);
      if (current && current.expiresAt > now) return { value: false };
      return {
        value: true,
        lease: { owner, expiresAt: now + leaseMs },
      };
    });
    if (acquired) {
      let leaseLost = false;
      let renewalPending: Promise<void> | null = null;
      const renewalInterval = globalThis.setInterval(() => {
        if (renewalPending) return;
        const renewalNow = Date.now();
        const renewal = mutateIndexedDbLease(leaseKey, maximumWaitMs, (currentValue) => {
          const current = parseLeaseValue(currentValue, renewalNow, leaseMs, futureSkewMs);
          if (current?.owner !== owner) return { value: false };
          return {
            value: true,
            lease: { owner, expiresAt: renewalNow + leaseMs },
          };
        }).then((renewed) => {
          if (!renewed) leaseLost = true;
        }, () => {
          leaseLost = true;
        }).finally(() => {
          if (renewalPending === renewal) renewalPending = null;
        });
        renewalPending = renewal;
      }, Math.max(50, Math.floor(leaseMs / 3)));
      try {
        const result = await operation();
        // Do not report a successful save while the final renewal is still
        // unresolved. It may have discovered that another context owns the
        // lease after a long renderer stall.
        if (renewalPending) await renewalPending;
        const stillOwned = await mutateIndexedDbLease(
          leaseKey,
          maximumWaitMs,
          (currentValue) => {
            const current = parseLeaseValue(currentValue, Date.now(), leaseMs, futureSkewMs);
            return { value: current?.owner === owner };
          },
        );
        if (!stillOwned) leaseLost = true;
        if (leaseLost) throw new Error('Lost local game-data lock while saving');
        return result;
      } finally {
        globalThis.clearInterval(renewalInterval);
        await mutateIndexedDbLease(leaseKey, maximumWaitMs, (currentValue) => {
          const current = parseLeaseValue(currentValue, Date.now(), leaseMs, futureSkewMs);
          return current?.owner === owner
            ? { value: true, remove: true }
            : { value: false };
        }).catch(() => undefined);
      }
    }
    await delay(retryMs);
  }
  throw new Error('Timed out waiting for local game-data lock');
}

interface LeaseMutationResult<T> {
  value: T;
  lease?: OriginLease;
  remove?: boolean;
}

async function mutateIndexedDbLease<T>(
  leaseKey: string,
  timeoutMs: number,
  mutate: (current: unknown) => LeaseMutationResult<T>,
): Promise<T> {
  const database = await openLockDatabase(timeoutMs);
  try {
    return await new Promise<T>((resolve, reject) => {
      const transaction = database.transaction(LOCK_STORE_NAME, 'readwrite');
      const store = transaction.objectStore(LOCK_STORE_NAME);
      const request = store.get(leaseKey);
      let decided = false;
      let result!: T;
      const timeout = globalThis.setTimeout(() => {
        try {
          transaction.abort();
        } catch {
          // The transaction may already have completed.
        }
        reject(new OriginLockBackendUnavailableError('Origin lock transaction timed out'));
      }, Math.max(250, timeoutMs));
      const finish = (error?: Error): void => {
        globalThis.clearTimeout(timeout);
        if (error) reject(error);
        else if (decided) resolve(result);
        else reject(new OriginLockBackendUnavailableError('Origin lock transaction made no decision'));
      };
      request.addEventListener('success', () => {
        try {
          const decision = mutate(request.result);
          result = decision.value;
          decided = true;
          if (decision.remove) store.delete(leaseKey);
          else if (decision.lease) store.put(decision.lease, leaseKey);
        } catch (error) {
          try {
            transaction.abort();
          } catch {
            // The error below still rejects the operation.
          }
          finish(error instanceof Error ? error : new Error('Origin lock mutation failed'));
        }
      }, { once: true });
      request.addEventListener('error', () => {
        finish(new OriginLockBackendUnavailableError('Origin lock read failed'));
      }, { once: true });
      transaction.addEventListener('complete', () => finish(), { once: true });
      transaction.addEventListener('abort', () => {
        finish(new OriginLockBackendUnavailableError('Origin lock transaction aborted'));
      }, { once: true });
      transaction.addEventListener('error', () => {
        finish(new OriginLockBackendUnavailableError('Origin lock transaction failed'));
      }, { once: true });
    });
  } finally {
    database.close();
  }
}

function openLockDatabase(timeoutMs: number): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    let request: IDBOpenDBRequest;
    try {
      request = indexedDB.open(LOCK_DATABASE_NAME, LOCK_DATABASE_VERSION);
    } catch {
      reject(new OriginLockBackendUnavailableError('Origin lock database unavailable'));
      return;
    }
    let settled = false;
    const timeout = globalThis.setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(new OriginLockBackendUnavailableError('Origin lock database did not open'));
    }, Math.max(250, Math.min(timeoutMs, 2_000)));
    request.addEventListener('upgradeneeded', () => {
      if (!request.result.objectStoreNames.contains(LOCK_STORE_NAME)) {
        request.result.createObjectStore(LOCK_STORE_NAME);
      }
    });
    request.addEventListener('success', () => {
      if (settled) {
        request.result.close();
        return;
      }
      settled = true;
      globalThis.clearTimeout(timeout);
      resolve(request.result);
    }, { once: true });
    request.addEventListener('error', () => {
      if (settled) return;
      settled = true;
      globalThis.clearTimeout(timeout);
      reject(new OriginLockBackendUnavailableError('Origin lock database failed to open'));
    }, { once: true });
    request.addEventListener('blocked', () => {
      if (settled) return;
      settled = true;
      globalThis.clearTimeout(timeout);
      reject(new OriginLockBackendUnavailableError('Origin lock database is blocked'));
    }, { once: true });
  });
}

async function withStorageLease<T>(
  leaseKey: string,
  operation: () => T | Promise<T>,
  options: OriginLockOptions,
): Promise<T> {
  if (typeof localStorage === 'undefined') return operation();
  const leaseMs = positive(options.leaseMs, DEFAULT_LEASE_MS);
  const retryMs = positive(options.retryMs, DEFAULT_RETRY_MS);
  const maximumWaitMs = positive(options.maximumWaitMs, leaseMs + 1_000);
  const futureSkewMs = nonNegative(options.futureSkewMs, DEFAULT_FUTURE_SKEW_MS);
  const owner = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  const waitStartedAt = monotonicNow();

  while (monotonicNow() - waitStartedAt <= maximumWaitMs) {
    const now = Date.now();
    let operationStarted = false;
    try {
      const raw = localStorage.getItem(leaseKey);
      const current = parseLease(raw, now, leaseMs, futureSkewMs);
      if (!current || current.expiresAt <= now) {
        if (raw && !current) localStorage.removeItem(leaseKey);
        localStorage.setItem(leaseKey, JSON.stringify({
          owner,
          expiresAt: now + leaseMs,
        } satisfies OriginLease));
        if (parseLease(localStorage.getItem(leaseKey), now, leaseMs, futureSkewMs)?.owner === owner) {
          let leaseLost = false;
          const renewalInterval = globalThis.setInterval(() => {
            try {
              const renewalNow = Date.now();
              const active = parseLease(
                localStorage.getItem(leaseKey),
                renewalNow,
                leaseMs,
                futureSkewMs,
              );
              if (active?.owner !== owner) {
                leaseLost = true;
                return;
              }
              localStorage.setItem(leaseKey, JSON.stringify({
                owner,
                expiresAt: renewalNow + leaseMs,
              } satisfies OriginLease));
            } catch {
              leaseLost = true;
            }
          }, Math.max(50, Math.floor(leaseMs / 3)));
          try {
            operationStarted = true;
            const result = await operation();
            const active = parseLease(
              localStorage.getItem(leaseKey),
              Date.now(),
              leaseMs,
              futureSkewMs,
            );
            if (leaseLost || active?.owner !== owner) {
              throw new Error('Lost local game-data lock while saving');
            }
            return result;
          } finally {
            globalThis.clearInterval(renewalInterval);
            if (parseLease(
              localStorage.getItem(leaseKey),
              Date.now(),
              leaseMs,
              futureSkewMs,
            )?.owner === owner) {
              localStorage.removeItem(leaseKey);
            }
          }
        }
      }
    } catch (error) {
      if (operationStarted) throw error;
      // Persistence is unavailable, so there is no durable cross-tab state to
      // protect. Keep the in-memory game path usable.
      return operation();
    }
    await delay(retryMs);
  }
  throw new Error('Timed out waiting for local game-data lock');
}

function parseLease(
  raw: string | null,
  now: number,
  leaseMs: number,
  futureSkewMs: number,
): OriginLease | null {
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as Partial<OriginLease>;
    return typeof value.owner === 'string'
      && value.owner.length > 0
      && typeof value.expiresAt === 'number'
      && Number.isFinite(value.expiresAt)
      && value.expiresAt <= now + leaseMs + futureSkewMs
      ? value as OriginLease
      : null;
  } catch {
    return null;
  }
}

function parseLeaseValue(
  value: unknown,
  now: number,
  leaseMs: number,
  futureSkewMs: number,
): OriginLease | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const candidate = value as Partial<OriginLease>;
  return typeof candidate.owner === 'string'
    && candidate.owner.length > 0
    && typeof candidate.expiresAt === 'number'
    && Number.isFinite(candidate.expiresAt)
    && candidate.expiresAt <= now + leaseMs + futureSkewMs
    ? candidate as OriginLease
    : null;
}

function monotonicNow(): number {
  return typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();
}

function positive(value: number | undefined, fallback: number): number {
  return Number.isFinite(value) && Number(value) > 0 ? Number(value) : fallback;
}

function nonNegative(value: number | undefined, fallback: number): number {
  return Number.isFinite(value) && Number(value) >= 0 ? Number(value) : fallback;
}

function delay(milliseconds: number): Promise<void> {
  return new Promise((resolve) => globalThis.setTimeout(resolve, milliseconds));
}

class OriginLockBackendUnavailableError extends Error {}
